import clsx from "clsx";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { BottomSheet } from "../../BottomSheet";
import { Button as ButtonV2 } from "../../Button";
import {
  CrossIcon,
  EmailPendingVerificationBadgeIcon,
  EmailPendingVerificationIcon,
} from "../../Icons";
import { ResponsiveModal } from "../../ResponsiveModal";
import { showErrorToast } from "../../Toast";
import { Typo } from "../../Typo";
import { SUPPORT_EMAIL } from "../consts";
import { withSupportLink } from "./supportLink";

export const VERIFY_EMAIL_POLL_MS = 5000;

export type VerifyEmailActions = {
  send: () => Promise<"sent" | "already_verified">;
  getVerified: () => Promise<boolean>;
  onVerified: () => void;
};

type VerifyEmailModalProps = {
  open: boolean;
  onClose: () => void;
  email?: string;
  onResend?: () => void;
  verification?: VerifyEmailActions | null;
  isMobile?: boolean;
};

export const VerifyEmailModal = ({
  open,
  onClose,
  email,
  onResend,
  verification,
  isMobile = false,
}: VerifyEmailModalProps) => {
  const { t } = useTranslation();
  const [sending, setSending] = useState(false);
  const autoSent = useRef(false);
  const verifiedRef = useRef(false);

  const verificationRef = useRef(verification);
  useEffect(() => {
    verificationRef.current = verification;
  });

  const finish = useCallback(() => {
    if (verifiedRef.current) return;
    verifiedRef.current = true;
    verificationRef.current?.onVerified();
  }, []);

  const send = useCallback(() => {
    const actions = verificationRef.current;
    if (!actions) return;
    setSending(true);
    actions
      .send()
      .then((result) => {
        if (result === "already_verified") finish();
      })
      .catch(() => {
        showErrorToast({
          title: t(
            "subIBReport_verifyEmailSendError",
            "Couldn't send the email. Try again.",
          ),
        });
      })
      .finally(() => setSending(false));
  }, [finish, t]);

  const hasVerification = Boolean(verification);

  useEffect(() => {
    if (!open || !hasVerification || autoSent.current) return;
    autoSent.current = true;
    send();
  }, [open, hasVerification, send]);

  useEffect(() => {
    if (!open || !hasVerification) return;
    verifiedRef.current = false;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const schedule = () => {
      timer = setTimeout(() => {
        const actions = verificationRef.current;
        if (cancelled || !actions) return;
        actions
          .getVerified()
          .then((verified) => {
            if (cancelled) return;
            if (verified) {
              finish();
              return;
            }
            schedule();
          })
          .catch(() => {
            if (!cancelled) schedule();
          });
      }, VERIFY_EMAIL_POLL_MS);
    };
    schedule();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [open, hasVerification, finish]);

  const closeLabel = t("common_close", "Close");

  const resendButton = (
    <ButtonV2
      variant="brand"
      size="md"
      onClick={verification ? send : onResend}
      isLoading={sending}
      className={clsx(isMobile && "w-full")}
      data-testid="verify_email_modal_resend_button"
    >
      {t("subIBReport_verifyEmailCta", "Resend email")}
    </ButtonV2>
  );

  const cancelButton = (
    <ButtonV2
      variant="tonal"
      size="md"
      onClick={onClose}
      className={clsx(isMobile && "w-full")}
      data-testid="verify_email_modal_cancel_button"
    >
      {closeLabel}
    </ButtonV2>
  );

  const helpText = withSupportLink(
    t(
      "subIBReport_verifyEmailHelp",
      "Can't find that email? Check your spam folder or contact {{supportEmail}}",
      { supportEmail: SUPPORT_EMAIL },
    ),
    { testId: "verify_email_modal_support_link" },
  );

  const renderLead = () => {
    if (!email) {
      return t(
        "subIBReport_verifyEmailLeadNoEmail",
        "Check your email to verify your email address to perform this action",
      );
    }
    const lead = t(
      "subIBReport_verifyEmailLead",
      "Check {{email}} to verify your email address to perform this action",
      { email },
    );
    const [beforeEmail, ...afterEmail] = lead.split(email);
    if (afterEmail.length === 0) return lead;
    return (
      <>
        {beforeEmail}
        <Typo
          element="span"
          type={isMobile ? "labelSM" : "labelMD"}
          textColor="text-text-primary"
        >
          {email}
        </Typo>
        {afterEmail.join(email)}
      </>
    );
  };

  const body = (
    <div className="flex flex-col gap-4">
      <div className="relative size-16 shrink-0">
        <div className="absolute inset-[20.1%_12.62%_29.87%_18.52%] drop-shadow-[5.333px_5.333px_10.667px_rgba(0,100,250,0.15)]">
          <EmailPendingVerificationIcon
            className="size-full"
            aria-hidden="true"
            focusable="false"
          />
        </div>
        <div className="absolute inset-[52.4%_51.07%_5.94%_7.27%] overflow-hidden rounded-full backdrop-blur-[9.33px]">
          <EmailPendingVerificationBadgeIcon
            className="size-full"
            aria-hidden="true"
            focusable="false"
          />
        </div>
      </div>
      <Typo
        element="h2"
        type={isMobile ? "headingLG" : "headingXL"}
        textColor="text-text-primary"
      >
        {t("subIBReport_verifyEmailTitle", "First, verify your email")}
      </Typo>
      <Typo
        element="p"
        type={isMobile ? "bodySM" : "bodyMD"}
        textColor="text-text-secondary"
      >
        {renderLead()}
      </Typo>
      <Typo
        element="p"
        type={isMobile ? "bodyXS" : "bodySM"}
        textColor="text-text-secondary"
      >
        {helpText}
      </Typo>
    </div>
  );

  if (isMobile) {
    return (
      <BottomSheet
        isOpen={open}
        onClose={onClose}
        isFullScreen
        isDismissable={false}
        showCloseButton
        closeButtonTestId="verify_email_modal_close_button"
        contentContainerClassName="py-3"
        footer={
          <div className="flex flex-col gap-2">
            {resendButton}
            {cancelButton}
          </div>
        }
      >
        {body}
      </BottomSheet>
    );
  }

  return (
    <ResponsiveModal
      isOpen={open}
      onClose={onClose}
      isMobile={false}
      headerClassName="!hidden"
      footerContainerClassName="!mt-8"
      footer={
        <div className="flex justify-end gap-4">
          {cancelButton}
          {resendButton}
        </div>
      }
    >
      <div className="flex flex-col">
        <div className="flex justify-end">
          <button
            type="button"
            aria-label={closeLabel}
            onClick={onClose}
            data-testid="verify_email_modal_close_button"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-icon-secondary transition-colors hover:bg-overlay-opacity-state-hover"
          >
            <CrossIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
        {body}
      </div>
    </ResponsiveModal>
  );
};
